import { CaretRightOutlined } from '@ant-design/icons';
import { isEmpty, kebabCase, map } from 'lodash';
import React from 'react';
import { useSelector } from 'react-redux';
import { ADP } from '../../../common/analytics/sourceTypes';
import dateTimeHelper from '../../../common/helpers/ui/DateTimeHelper';
import { getPostCategoryInThai } from '../../../common/helpers/domain/postCategories';
import { getContentPagePath } from '../../../common/helpers/routing/urlGenerator';
import contentResource from '../../../locales/th/content.json';
import {
  RecommendedDescription,
  RecommendedInfo,
  RecommendedItemWrapper,
  RecommendedPostType,
  RecommendedTitle,
  RecommendedWrapper,
  TitleWrapper,
  VideoWrapper,
} from './Recommended.style';
import { recommendedImageConfig } from '../../../common/helpers/sharpie/sharpie';
import { handleImageUrl } from '../../../common/helpers/sharpie/imageUrlHelper';
import { isVideoPost } from '../../../common/helpers/domain/videoTypes';
import { getArticleTypeInGA } from '../../../common/analytics/helpers/articleTypeHelper';
import { trackRecommendedVideo } from '../../../common/analytics/analyticsTracker';
import { ANALYTICS_FEATURES } from '../../../common/analytics/featureTypes';
import { WholeState } from '../../../store/common/type';
import { RecommendedData } from '../../../common/helpers/domain/recommendation';

interface RecommendedProps {
  isOnContentPage?: boolean;
  isOnPovPage?: boolean;
  className?: string;
  postRecommendedData?: RecommendedData;
}

const Recommended: React.FC<RecommendedProps> = ({
  isOnContentPage,
  isOnPovPage,
  className,
  postRecommendedData = {},
}) => {
  const { recommendedData = [] } = postRecommendedData;
  const postCategory = !isEmpty(recommendedData[0])
    ? recommendedData[0].postCategory
    : '';
  const isNextGenSupported = useSelector((state: WholeState) =>
    state.common !== undefined ? state.common.isNextGenSupported : false,
  );
  const originImageUrl = recommendedData[0]?.featuredImage?.url ?? '';
  const imageUrl = handleImageUrl(
    originImageUrl,
    recommendedImageConfig.width,
    recommendedImageConfig.height,
    isNextGenSupported,
  );
  const userInfo = useSelector((state: WholeState) =>
    state.common !== undefined ? state.common.user : {},
  );
  const renderRecommendedVideoContent = () => {
    return map(recommendedData, (item, i) => {
      const link = getContentPagePath({
        ...item,
      });
      const articleTypeInGA = getArticleTypeInGA(
        item.postCategory,
        item.videoType,
      );

      return (
        <VideoWrapper key={i}>
          <a
            href={link}
            onClick={() => {
              trackRecommendedVideo(
                userInfo,
                { ...item, articleTypeInGA },
                item.project,
                ADP.PAGE,
                ANALYTICS_FEATURES.RECOMMENDED,
              );
            }}
          >
            <CaretRightOutlined style={{ fontSize: 64, color: '#ffffff' }} />
            <img
              src={isVideoPost(item.videoType) ? imageUrl : ''}
              alt={item.title}
            />
          </a>
        </VideoWrapper>
      );
    });
  };

  const renderRecommendedVideoInfo = () => {
    const recommendedVideoByText = contentResource.recommendedVideo.videoBy;

    return map(recommendedData, (item, i) => {
      const link = getContentPagePath({ ...item });
      const xago = dateTimeHelper.getHumanizeDiff(item.date) || '';
      const articleTypeInGA = getArticleTypeInGA(
        item.postCategory,
        item.videoType,
      );

      return (
        <RecommendedDescription key={i}>
          <RecommendedPostType>
            {postCategory && <p>{getPostCategoryInThai(postCategory)}</p>}
          </RecommendedPostType>
          <RecommendedTitle>
            <h3>
              <a
                href={link}
                onClick={() =>
                  trackRecommendedVideo(
                    userInfo,
                    { ...item, articleTypeInGA },
                    item.project,
                    ADP.PAGE,
                    ANALYTICS_FEATURES.RECOMMENDED,
                  )
                }
              >
                {' '}
                {item.title}
              </a>
            </h3>
          </RecommendedTitle>
          <RecommendedInfo>
            <p>
              {recommendedVideoByText}:{' '}
              <a href={item.author.link ? item.author.link : '#'}>
                {item.author.name}
              </a>{' '}
              | <span>{xago}</span> |{' '}
              <span>
                {map(item.propertyType, (item, i) => [
                  i > 0 && ', ',
                  <a
                    data-test="recommended-video-property-type-link"
                    href={`/${kebabCase(item)}`}
                    key={i}
                  >
                    {item}
                  </a>,
                ])}
              </span>
            </p>
          </RecommendedInfo>
        </RecommendedDescription>
      );
    });
  };

  const recommendedVideoTitle = contentResource.recommendedVideo.title;
  return (
    recommendedData?.length > 0 && (
      <RecommendedWrapper
        isOnContentPage={isOnContentPage}
        className={className}
        data-test={'recommended'}
      >
        <TitleWrapper>
          <h2>{recommendedVideoTitle}</h2>
        </TitleWrapper>
        <RecommendedItemWrapper
          isOnContentPage={isOnContentPage}
          isOnPovPage={isOnPovPage}
        >
          {renderRecommendedVideoContent()}
          {renderRecommendedVideoInfo()}
        </RecommendedItemWrapper>
      </RecommendedWrapper>
    )
  );
};

export default Recommended;
